/**
 * What the market looks like, before what the account looks like.
 *
 * Three readings, and one of them is deliberately empty. Fear and Greed has no
 * first-party measure here and no route to a third-party one, so it shows the
 * gap and says why: a number invented to fill a tile is indistinguishable from
 * a measured one the moment it is on screen, and this product moves real money
 * on what it shows.
 *
 * The other two are Skryptos figures, computed from the same market pass the
 * ranking was cut from. Nothing here asks the venue anything, and nothing here
 * reaches a decision: whether a cycle opens is answered by the selector and the
 * Risk Engine, neither of which reads a screen.
 */
import { Gauge, type GaugeTone } from "@/design-system/Gauge";
import { Panel } from "@/design-system/Panel";
import type { IndicatorResponse, RankingResponse } from "@/shared/api";

const RISK_LABEL: Record<string, string> = {
  CALM: "Спокойный",
  NORMAL: "Обычный",
  ELEVATED: "Повышенный",
  HOSTILE: "Враждебный",
};

const CLIMATE_LABEL: Record<string, string> = {
  WEAK: "Слабый",
  SOFT: "Вялый",
  STEADY: "Ровный",
  STRONG: "Сильный",
};

/** Risk runs the other way from climate: a high score is a bad market. */
const RISK_TONE: Record<string, GaugeTone> = {
  CALM: "good",
  NORMAL: "neutral",
  ELEVATED: "caution",
  HOSTILE: "bad",
};

const CLIMATE_TONE: Record<string, GaugeTone> = {
  WEAK: "bad",
  SOFT: "caution",
  STEADY: "neutral",
  STRONG: "good",
};

const RISK_HELP =
  "Market Risk. Насколько сейчас опасно открывать новые LONG-циклы BASE v1. " +
  "Считается по медианам последнего прохода: односторонность падения, доля " +
  "невосстановившихся откатов, узость поля, спред.";

const CLIMATE_HELP =
  "Recovery Climate. Насколько рынок поддерживает модель «откат → восстановление», на которой " +
  "живёт BASE v1. Считается по медианам последнего прохода: доля " +
  "восстановлений, их скорость, устойчивость, ширина поля.";

/** The inputs behind a figure, so the tooltip can say what moved it. */
function why(indicator: IndicatorResponse, help: string): string {
  const parts = Object.entries(indicator.inputs).map(
    ([name, value]) => `${name} ${value.slice(0, 4)}`,
  );
  return `${help}\n\n${parts.join(" · ")}\n${indicator.version}`;
}

export function MarketIntelligence({
  ranking,
  testId = "market-intelligence",
}: {
  readonly ranking: RankingResponse | null;
  /**
   * Named by the caller because this block is mounted twice: under the
   * navigation on a wide screen, and in the page on a narrow one where there
   * is no navigation column to sit under. Only one is ever visible, and two
   * nodes answering to one name would make every query ambiguous.
   */
  readonly testId?: string;
}): React.JSX.Element {
  const climate = ranking?.climate ?? null;

  return (
    <Panel title="Рынок" labelledBy="market-heading" testId={testId}>
      <div className="flex flex-col gap-3">
        {/* A source that is not connected gets a line, not a tile. It was
            taking a third of a narrow column to say nothing, and the two
            indicators that do have readings were the ones being squeezed. */}
        {ranking?.fear_greed.score == null ? (
          <p
            data-testid="gauge-fear-greed"
            className="text-helper-sm text-text-muted"
            title="Индекс страха и жадности. Собственного источника у Skryptos нет, внешний не подключён — поэтому здесь честно пусто."
          >
            {`Fear & Greed — ${ranking?.fear_greed.unavailable_reason ?? "источник не подключён"}`}
          </p>
        ) : (
          <Gauge
            label="Fear & Greed"
            score={ranking.fear_greed.score}
            band={ranking.fear_greed.band}
            tone="unknown"
            testId="gauge-fear-greed"
          />
        )}

        <Gauge
          label="Риск рынка"
          score={climate?.market_risk.score ?? null}
          band={
            climate === null ? null : (RISK_LABEL[climate.market_risk.band] ?? climate.market_risk.band)
          }
          tone={climate === null ? "unknown" : (RISK_TONE[climate.market_risk.band] ?? "unknown")}
          absent="Рейтинг ещё не рассчитан"
          testId="gauge-market-risk"
          title={climate === null ? RISK_HELP : why(climate.market_risk, RISK_HELP)}
        />

        <Gauge
          label="Климат восстановления"
          score={climate?.recovery_climate.score ?? null}
          band={
            climate === null
              ? null
              : (CLIMATE_LABEL[climate.recovery_climate.band] ?? climate.recovery_climate.band)
          }
          tone={
            climate === null
              ? "unknown"
              : (CLIMATE_TONE[climate.recovery_climate.band] ?? "unknown")
          }
          absent="Рейтинг ещё не рассчитан"
          testId="gauge-recovery-climate"
          title={
            climate === null ? CLIMATE_HELP : why(climate.recovery_climate, CLIMATE_HELP)
          }
        />

        {climate !== null && (
          // A figure taken over four symbols is not the market. Said rather
          // than left for the reader to assume.
          <p className="text-helper-sm text-text-muted" data-testid="climate-measured">
            {/* Over what, not when. These come from the same pass as the
                ranking, and the strip above already carries that feed's age. */}
            {`По медианам ${String(climate.measured)} измеренных контрактов`}
          </p>
        )}
      </div>
    </Panel>
  );
}
